/* klaasje-buy
   -----------
   Smaakkeuze en aantal in de koopsectie. Het toevoegen zelf doet Dawn's
   <product-form>: die vangt de submit af en opent de winkelmand. Hier
   wordt alleen bijgehouden wélke variant in het verborgen veld staat,
   plus de prijs, de afbeelding en de knop.

   De hero kan hier van buitenaf een smaak aanwijzen: de koopknopjes over
   de drie blikjes roepen klaasjeSelectFlavor() aan met de naam van hun
   smaak. Die naam is het enige wat de twee secties delen; de koopsectie
   doet zelf het kiezen en het in beeld brengen, want die weet waar hij
   staat en wat er te zien hoort te zijn.

   Is de gekozen smaak uitverkocht, dan wisselt het hele onderste blok:
   geen winkelwagen maar "geef me een seintje", dat opengaat als een veld
   voor je mailadres. Dat is Shopify's eigen klantformulier; het adres komt
   binnen als klant met de tags 'seintje' en 'seintje-<smaak>'.
*/

const SEINTJE_KEY = 'klaasje_seintje_smaak';

/* Welk klantformulier van de pagina net verzonden is. Shopify's
   form.posted_successfully? hoort bij de pagina en niet bij het formulier,
   dus de nieuwsbrief in de footer zette hier anders dezelfde melding aan en
   scrolde de bezoeker omhoog. Dezelfde sleutel staat in klaasje-news.js. */
const VERZONDEN_KEY = 'klaasje_form_verzonden';

class KlaasjeBuy extends HTMLElement {
  connectedCallback() {
    this.variants = this.readVariants();

    this.priceEl = this.querySelector('[data-klaasje-buy-price]');
    this.imageEl = this.querySelector('[data-klaasje-buy-image]');
    this.idInput = this.querySelector('[data-klaasje-buy-id]');
    this.addBtn = this.querySelector('[data-klaasje-buy-add]');
    this.qtyInput = this.querySelector('[data-klaasje-qty-input]');

    this.cartEl = this.querySelector('[data-klaasje-buy-cart]');
    this.notifyEl = this.querySelector('[data-klaasje-buy-notify]');
    this.flagEl = this.querySelector('[data-klaasje-soldout-flag]');

    /* Alle smaakknoppen, ook die van de voorbeeldstand: zonder gekozen
       product valt er niets te kopen, maar de keuze hoort wel te wisselen. */
    this.flavors = Array.from(this.querySelectorAll('.klaasje-flavor'));
    this.flavors.forEach((btn) => {
      btn.addEventListener('click', () => this.select(btn));
    });

    const down = this.querySelector('[data-klaasje-qty-down]');
    const up = this.querySelector('[data-klaasje-qty-up]');
    if (down) down.addEventListener('click', () => this.step(-1));
    if (up) up.addEventListener('click', () => this.step(1));
    if (this.qtyInput) this.qtyInput.addEventListener('change', () => this.step(0));

    this.bindNotify();
    this.step(0);

    // de smaak die al gekozen staat bepaalt met welk blok we beginnen
    const active = this.flavors.find((btn) => btn.classList.contains('is-active'));
    if (active) this.mode(active);

    this.restoreNotify();
  }

  /* ── Van buitenaf een smaak aanwijzen ─────────────────────── */

  /* Publiek, met een eigen voorvoegsel zodat de naam nooit botst met iets
     wat een browser zelf op een element zet (zie CLAUDE.md over `cue` en
     `dir`). Geeft terug of de smaak gevonden is; heen scrollen doen we
     hoe dan ook, want de bezoeker heeft om de koopsectie gevraagd. */
  klaasjeSelectFlavor(name) {
    const btn = this.matchFlavor(name);
    if (btn) this.select(btn);
    this.scrollHere();
    return Boolean(btn);
  }

  /* Eerst op de hele naam, dan losjes. Zo blijft "witte druif" werken als
     de variant in Shopify straks "Witte druif 250ml" heet, zonder dat er
     ergens een tabel met vertalingen bijkomt. */
  matchFlavor(name) {
    const wanted = String(name || '').trim().toLowerCase();
    if (!wanted || !this.flavors) return null;

    const label = (btn) => (btn.dataset.klaasjeFlavorName || btn.textContent || '').trim().toLowerCase();
    return (
      this.flavors.find((btn) => label(btn) === wanted) ||
      this.flavors.find((btn) => {
        const own = label(btn);
        return own && (own.includes(wanted) || wanted.includes(own));
      }) ||
      null
    );
  }

  /* De sectie in beeld brengen: de bovenrand van dit witte vlak precies op
     de bovenrand van het scherm. Dat is de naad waar het verloop van de
     hero overgaat in het wit, en dat is wat je wilt zien staan. Er gaat
     dus bewust geen ruimte voor de header vanaf: de pill zweeft over de
     lucht die deze sectie zelf al bovenin heeft.

     Op een smal scherm staat de smaakkeuze onder de foto en zou je bij de
     bovenkant van de sectie de knoppen nog niet zien; dan scrollen we net
     zover door dat ze wél in beeld staan. Anders zie je niet wat je
     zojuist gekozen hebt. */
  scrollHere() {
    let top = this.getBoundingClientRect().top + window.scrollY;

    const flavors = this.querySelector('.klaasje-buy__flavors');
    if (flavors) {
      const need = flavors.getBoundingClientRect().bottom + window.scrollY - window.innerHeight + 32;
      top = Math.max(top, need);
    }

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(top, 0), behavior: reduced ? 'auto' : 'smooth' });
  }

  readVariants() {
    const tag = this.querySelector('[data-klaasje-buy-variants]');
    if (!tag) return [];
    try {
      return JSON.parse(tag.textContent);
    } catch (e) {
      return [];
    }
  }

  select(btn) {
    this.flavors.forEach((other) => {
      const active = other === btn;
      other.classList.toggle('is-active', active);
      other.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.mode(btn);

    const id = btn.dataset.klaasjeVariant;
    const variant = this.variants.find((v) => String(v.id) === String(id));
    // voorbeeldstand: er is geen variant, dus verder valt er niets bij te werken
    if (!variant) return;

    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.disabled = !variant.available;
    }
    if (this.priceEl) this.priceEl.innerHTML = variant.price;
    if (this.imageEl && variant.image) this.imageEl.src = variant.image;

    if (this.addBtn) {
      this.addBtn.disabled = !variant.available;
      // aria-disabled kan blijven staan van een eerdere verzending
      this.addBtn.removeAttribute('aria-disabled');
      this.addBtn.classList.remove('loading');
    }
  }

  /* Kopen of een seintje. De voorraad komt uit de variant; staat er geen
     product, dan zegt de knop het zelf via data-klaasje-soldout. */
  mode(btn) {
    const id = btn.dataset.klaasjeVariant;
    const variant = this.variants.find((v) => String(v.id) === String(id));
    const available = variant ? variant.available : btn.dataset.klaasjeSoldout === undefined;

    if (this.flagEl) this.flagEl.hidden = available;
    if (this.cartEl) this.cartEl.hidden = !available;
    if (this.notifyEl) this.notifyEl.hidden = available;

    // Elke smaak krijgt zijn eigen tag, zodat je in de admin ziet op welke
    // smaak iemand wacht. Terug naar de dichte knop, want dit is een andere
    // smaak dan waar het veld eventueel voor openstond.
    if (!available) {
      const naam = (btn.dataset.klaasjeFlavorName || btn.textContent || '').trim().toLowerCase();
      const slug = naam.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (this.tagsInput) this.tagsInput.value = slug ? `seintje, seintje-${slug}` : 'seintje';
      this.closeNotify();
    }
  }

  bindNotify() {
    this.notifyForm = this.querySelector('.klaasje-notify__form');
    if (!this.notifyForm) return;

    this.tagsInput = this.querySelector('[data-klaasje-notify-tags]');
    this.notifyOpen = this.querySelector('[data-klaasje-notify-open]');
    this.notifyRow = this.querySelector('[data-klaasje-notify-row]');
    this.notifyInput = this.querySelector('[data-klaasje-notify-input]');
    this.notifySend = this.querySelector('[data-klaasje-notify-send]');
    this.notifyMsg = this.querySelector('[data-klaasje-notify-msg]');

    this.notifyOpen?.addEventListener('click', () => {
      this.notifyOpen.hidden = true;
      if (this.notifyRow) this.notifyRow.hidden = false;
      this.notifyInput?.focus();
    });

    this.notifyForm.addEventListener('submit', () => this.sendNotify());
  }

  closeNotify() {
    if (!this.notifyForm) return;
    if (this.notifyOpen) this.notifyOpen.hidden = false;
    if (this.notifyRow) this.notifyRow.hidden = true;
    if (this.notifyMsg) this.notifyMsg.hidden = true;
    if (this.notifyInput) this.notifyInput.value = '';
    this.notifyForm.classList.remove('is-sending');
  }

  /* Het formulier gaat gewoon weg, zodat Shopify er zijn captcha bij kan
     zetten (met fetch werd het geweigerd, zie de Liquid). Hier alleen
     onthouden welke smaak het was, want na het herladen staat de
     standaardsmaak weer gekozen. Een ongeldig adres komt hier niet: dan
     houdt de browser de verzending zelf al tegen. */
  sendNotify() {
    const active = this.flavors.find((btn) => btn.classList.contains('is-active'));
    const naam = active ? (active.dataset.klaasjeFlavorName || active.textContent || '').trim() : '';
    try {
      sessionStorage.setItem(SEINTJE_KEY, naam);
      sessionStorage.setItem(VERZONDEN_KEY, this.notifyForm.id);
    } catch (e) {
      // geen sessionStorage: dan staat na het herladen de standaardsmaak, de melding komt wel
    }
    this.notifyForm.classList.add('is-sending');
  }

  /* Was deze verzending van ons? De uitslag die Shopify meegeeft geldt voor
     élk klantformulier op de pagina, dus zonder deze controle toonde een
     aanmelding voor de nieuwsbrief in de footer hier het seintje en scrolde
     de pagina omhoog. Vandaar de id die sendNotify wegschrijft; kan er geen
     sessionStorage bij, dan blijft de anchor van Shopify's redirect over
     (/contact#<form-id>). */
  vanOnsFormulier() {
    if (!this.notifyForm) return false;
    let id = null;
    try {
      id = sessionStorage.getItem(VERZONDEN_KEY);
    } catch (e) {
      // zie sendNotify
    }
    if (id !== null) return id === this.notifyForm.id;
    return window.location.hash === `#${this.notifyForm.id}`;
  }

  /* Na het herladen: Shopify geeft de uitslag mee in de Liquid
     (data-klaasje-notify-status). Smaak terugzetten, melding tonen en de
     sectie in beeld brengen. */
  restoreNotify() {
    const status = this.notifyMsg && this.notifyMsg.dataset.klaasjeNotifyStatus;
    if (!status) return;
    if (!this.vanOnsFormulier()) return;

    let naam = '';
    try {
      naam = sessionStorage.getItem(SEINTJE_KEY) || '';
      sessionStorage.removeItem(SEINTJE_KEY);
    } catch (e) {
      // zie sendNotify
    }
    const btn = naam && this.matchFlavor(naam);
    if (btn) this.select(btn);

    if (this.notifyEl) this.notifyEl.hidden = false;
    if (this.notifyOpen) this.notifyOpen.hidden = true;
    if (this.notifyRow) this.notifyRow.hidden = status !== 'fout';
    this.notifyMsg.hidden = false;

    requestAnimationFrame(() => this.scrollHere());
  }

  /* Houdt het aantal op een heel getal van minimaal 1 en zet de min uit
     zodra je onderaan bent. */
  step(delta) {
    if (!this.qtyInput) return;
    const current = parseInt(this.qtyInput.value, 10);
    const next = Math.max(1, (isNaN(current) ? 1 : current) + delta);
    this.qtyInput.value = next;

    const down = this.querySelector('[data-klaasje-qty-down]');
    if (down) down.disabled = next <= 1;
  }
}

if (!customElements.get('klaasje-buy')) {
  customElements.define('klaasje-buy', KlaasjeBuy);
}
